import { ChangeEvent, useRef, type ComponentProps } from 'react';

import { useDebouncedCallback } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import {
  useImportHubNavigate,
  useImportHubPaginatedSearch,
} from '../../hooks/useImportHubRouteApi.js';

export const RouterSearchBar = ({
  onChange,
  className,
  ...rest
}: Partial<ComponentProps<typeof SearchBar>>) => {
  const { t } = useTranslation('@myn/import-hub');
  const navigate = useImportHubNavigate();
  const searchParams = useImportHubPaginatedSearch();

  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchChange = useDebouncedCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      navigate({
        search: (prev) => ({
          ...prev,
          search: e.target.value,
          pageNumber: 1,
        }),
      } as Parameters<typeof navigate>[0]);
    },
    300,
  );

  const handleSearchReset = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
      navigate({
        search: (prev) => ({
          ...prev,
          search: '',
          pageNumber: 1,
        }),
      } as Parameters<typeof navigate>[0]);
    }
  };

  return (
    <SearchBar
      ref={searchInputRef}
      defaultValue={searchParams.search}
      onChange={handleSearchChange}
      onClear={handleSearchReset}
      placeholder={t('Search')}
      size="medium"
      className={cn('shrink-0', className)}
      {...rest}
    />
  );
};
